import React from "react";
import type { AssessmentQuestion } from "../../axios/quiz";
import type { ContentBlock } from "../../types";
import { assessmentQuestionToContentBlock } from "../../utils/assessmentInteractive";
import { FillInBlankBlock } from "./fill-in-blank-block";
import { OrderingBlock } from "./ordering-block";
import { MatchingBlock } from "./matching-block";
import { OrderedResponseBlock } from "./ordered-response-block";
import type { AssessmentInteractiveModeProps } from "./assessment-mode";

interface AssessmentInteractiveQuestionProps
  extends AssessmentInteractiveModeProps {
  question: AssessmentQuestion;
  isAnswerChecked?: boolean;
  checkedState?: {
    userAnswers?: Record<string, string>;
    userOrder?: string[];
    userPairs?: Array<{ left_id: string; right_id: string }>;
    isCorrect?: boolean;
    feedback?: string;
  };
}

const ASSESSMENT_STUB_IDS = {
  messageId: "assessment",
  userId: "assessment",
  tabId: "assessment",
};

export const AssessmentInteractiveQuestion: React.FC<
  AssessmentInteractiveQuestionProps
> = ({
  question,
  isAnswerChecked = false,
  checkedState,
  hideSubmitButton = true,
  hideFeedback = true,
  hideResultHighlight = false,
  submitGetterRef,
  onCompletionChange,
}) => {
  const block = assessmentQuestionToContentBlock(question, {
    state: isAnswerChecked ? "disable" : "default",
    userAnswers: checkedState?.userAnswers,
    userOrder: checkedState?.userOrder,
    userPairs: checkedState?.userPairs,
    isCorrect: hideResultHighlight ? undefined : checkedState?.isCorrect,
    feedback: hideResultHighlight ? undefined : checkedState?.feedback,
  });

  if (!block) {
    return null;
  }

  const commonProps = {
    blockIndex: 0,
    ...ASSESSMENT_STUB_IDS,
    isAssessmentMode: true,
    hideSubmitButton,
    hideFeedback,
    hideResultHighlight,
    submitGetterRef,
    onCompletionChange,
  };

  return renderAssessmentBlock(block, commonProps);
};

function renderAssessmentBlock(
  block: ContentBlock,
  commonProps: {
    blockIndex: number;
    messageId: string;
    userId: string;
    tabId: string;
    hideSubmitButton?: boolean;
    hideFeedback?: boolean;
    hideResultHighlight?: boolean;
    submitGetterRef?: AssessmentInteractiveModeProps["submitGetterRef"];
    onCompletionChange?: (isComplete: boolean) => void;
  },
) {
  switch (block.type) {
    case "fill_in_blank":
      return <FillInBlankBlock block={block} {...commonProps} />;
    case "ordering":
      return <OrderingBlock block={block} {...commonProps} />;
    case "matching":
      return <MatchingBlock block={block} {...commonProps} />;
    case "ordered_response":
      return <OrderedResponseBlock block={block} {...commonProps} />;
    default:
      return null;
  }
}
